/**
 * 性能优化工具函数
 * 用于延迟加载非关键资源、处理懒加载、预加载等性能优化
 */

/**
 * 延迟加载非关键JavaScript
 * @param {string} src - 要加载的JavaScript文件路径
 * @param {boolean} async - 是否异步加载
 * @param {boolean} defer - 是否延迟加载
 */
export const loadScriptAsync = (src, async = true, defer = true) => {
  const script = document.createElement('script');
  script.src = src;
  script.async = async;
  script.defer = defer;
  document.body.appendChild(script);
};

/**
 * 延迟加载非关键CSS
 * @param {string} href - 要加载的CSS文件路径
 */
export const loadStyleAsync = (href) => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

/**
 * 监听元素进入视口后执行回调
 * @param {Element} element - DOM元素
 * @param {Function} callback - 元素进入视口时执行的回调
 * @param {Object} options - IntersectionObserver配置项
 */
export const onElementVisible = (element, callback, options = {}) => {
  if (!('IntersectionObserver' in window)) {
    // 旧浏览器降级处理
    callback();
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback();
        observer.disconnect();
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px',
    ...options
  });

  observer.observe(element);
};

/**
 * 预加载关键资源
 * @param {Array<string>} urls - 要预加载的资源URL数组
 * @param {string} type - 资源类型: 'image', 'script', 'style', 'font'
 */
export const preloadResources = (urls, type) => {
  if (!urls || !urls.length) return;

  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = type;
    link.href = url;
    
    if (type === 'font') {
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  });
};

/**
 * 实现图片懒加载
 * 此函数会查找并初始化所有具有data-src属性的img元素
 */
export const initLazyImages = () => {
  if (!('IntersectionObserver' in window)) {
    // 旧浏览器降级处理
    document.querySelectorAll('img[data-src]').forEach(img => {
      img.src = img.dataset.src;
    });
    return;
  }

  const lazyImageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const lazyImage = entry.target;
        lazyImage.src = lazyImage.dataset.src;
        lazyImage.removeAttribute('data-src');
        lazyImageObserver.unobserve(lazyImage);
      }
    });
  });

  document.querySelectorAll('img[data-src]').forEach(lazyImage => {
    lazyImageObserver.observe(lazyImage);
  });
};

/**
 * 将长任务分解为小任务，防止阻塞主线程
 * @param {Array} items - 要处理的项目数组
 * @param {Function} process - 处理每个项目的函数
 * @param {Function} callback - 所有项目处理完成后的回调
 */
export const processBatch = (items, process, callback) => {
  const batchSize = 5; // 每批处理数量
  const totalItems = items.length;
  let processedItems = 0;
  
  const processBatchItems = (startIndex) => {
    const endIndex = Math.min(startIndex + batchSize, totalItems);
    
    for (let i = startIndex; i < endIndex; i++) {
      process(items[i], i);
      processedItems++;
    }
    
    if (processedItems < totalItems) {
      // 使用requestIdleCallback或setTimeout进行下一批处理
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => processBatchItems(endIndex));
      } else {
        setTimeout(() => processBatchItems(endIndex), 1);
      }
    } else if (callback) {
      callback();
    }
  };
  
  processBatchItems(0);
};

/**
 * 添加浏览器缓存控制头部函数
 * 注意: 此函数需要在服务器端支持
 * @param {Object} res - 响应对象
 * @param {number} maxAge - 缓存最大时间(秒)
 */
export const addCacheControlHeaders = (res, maxAge = 86400) => {
  if (!res || !res.setHeader) return;
  
  res.setHeader('Cache-Control', `public, max-age=${maxAge}`);
  res.setHeader('Expires', new Date(Date.now() + maxAge * 1000).toUTCString());
};

/**
 * 检测并减少重排和重绘操作
 * @param {Function} callback - 包含DOM操作的回调函数
 */
export const batchDOMUpdates = (callback) => {
  if (!callback) return;
  
  // 获取元素的布局信息，强制重排在批量修改前发生
  // eslint-disable-next-line no-unused-vars
  const forceReflow = document.body.offsetHeight;
  
  // 执行批量DOM更新
  requestAnimationFrame(() => {
    callback();
  });
};

/**
 * 用于初始化所有性能优化功能
 */
export const initPerformanceOptimizations = () => {
  // 初始化懒加载图片
  initLazyImages();
  
  // 延迟加载第三方脚本
  setTimeout(() => {
    // 假设的第三方分析脚本
    loadScriptAsync('https://analytics.example.com/tracker.js');
  }, 3000);
  
  // 预加载关键字体
  preloadResources(['https://fonts.googleapis.com/css?family=Roboto:400,500,700'], 'font');
  
  // 监听首屏渲染
  if ('performance' in window) {
    window.addEventListener('load', () => {
      // First Contentful Paint
      const paintMetrics = performance.getEntriesByType('paint');
      const fcpEntry = paintMetrics.find(entry => entry.name === 'first-contentful-paint');
      
      if (fcpEntry) {
        console.log(`First Contentful Paint: ${fcpEntry.startTime}ms`);
      }
    });
  }
};

export default {
  loadScriptAsync,
  loadStyleAsync,
  onElementVisible,
  preloadResources,
  initLazyImages,
  processBatch,
  addCacheControlHeaders,
  batchDOMUpdates,
  initPerformanceOptimizations
};